import classNames from "classnames";
import { parse as optimisticParse } from "best-effort-json-parser";
import React, { ReactElement, useState, useCallback, ChangeEventHandler, useRef, useMemo, useEffect } from "react";
import { MainHeaderSearchState, MainHeaderSearchStateProps } from "./MainHeaderSearch.types";
import { Conditional } from "src/components/Conditional";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { localize, localizeFormat, getTypeName } from "src/l10n";
import { UnstyledButton, SpintrUser, setDisplayHeaderSearch, setOverlayId } from "src/ui";
import { SpintrTypes } from "src/typings";
import { executeSearchAction, ISearchParams, loadCachedSearchResult, setSearchAiSummary, clearSearchResults, submitFeedback, setSearchAiSearchFeedback, setSearchAiSearchFeedbackMessage, SearchActionTypes } from "src/search";
import { useDispatch, useSelector } from "react-redux";
import SpintrLoader from "src/ui/components/Loader";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { SmallBodyBolder, CaptionBolder, SmallBody, CaptionBody } from "src/components/Text";
import { checksum, capitalizeFirstLetter, mixpanelTrack } from "src/utils";
import api from "src/spintr/SpintrApi";
import qs from "qs";
import { AxiosResponse } from "axios";
import { useLocation } from "react-router";
import { SearchLoader } from "../SearchLoader";
import { DirectionalHint, FocusTrapCallout, Icon, Shimmer, ShimmerGap, Stack, StackItem, TextField, useWindow } from "@fluentui/react";
import { GoogleFilesRequest, GoogleFilesResponse, ISearchFeedback } from "src/search/types";
import ModernSearchResultObjectHit from "src/search/components/ModernSearchResultObjectHit/ModernSearchResultObjectHit";
import { Link } from "react-router-dom";
import { searchFiles } from "src/office365/office365-api";
import { from, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { TextBubble } from "src/ui/components";
import { v4 as uuid } from "uuid";
import { Label } from "src/ui/components/Label/Label";
import FormControl from "src/ui/components/Forms/FormControl";

/**
 * MainHeaderSearch component
 *
 * @returns {ReactElement | null} The rendered component
 */
function MainHeaderSearch(): ReactElement | null {
    const dispatch = useDispatch();
    const location = useLocation();
    const _window = useWindow();
    const ref = useRef<HTMLDivElement>(null);
    const currentChecksum = useRef<number | null>(null);
    const currentFeedbackId = useRef<string | null>(null);
    const stateProps = useSelector<Spintr.AppState, MainHeaderSearchStateProps>((state) => ({
        results: state.search.currentResults,
        aiResults: state.search.currentAiResults,
        isSearching: state.search.isLoadingResults,
        isSearchingMore: state.search.isLoadingMoreResults,
        headers: state.search.currentHeaders,
        lastParams: state.search.lastSuccessfulParams,
        history: state.search.history,
        errorMessageTag: state.search.errorMessageTag,
        displayHeaderSearch: state.ui.displayHeaderSearch,
        headerColor: state.instance.get("useColorHeader") ? state.instance.get("headerColor") ?? "#FFFFFF" : "#FFFFFF",
        office365Enabled:
            (state.instance.get("office365Enabled") as boolean) && state.profile.active.office365Connected,
        enableSharepoint: state.instance.get("enableSharepoint"),
        enableOnedrive: state.instance.get("enableOnedrive"),
        googleDriveEnabled: (state.instance.get("googleDriveEnabled") as boolean) && state.profile.active.googleConnected,
        aiSearchEnabledUberTypes: (state.instance.get("enableAiSearchForUberTypes") as SpintrTypes.UberType[]) || [],
    }));

    const [state, setState] = useState<MainHeaderSearchState>({
        queryText: "",
        isSearchingQueryText: "",
        typeId: 0,
        isLoadingOffice365: false,
        office365Count: 0,
        office365HasMore: false,
        googleIsLoading: false,
        googleCount: 0,
        googleHasMore: false,
    });

    const [minResultsHeight, setMinResultsHeight] = useState<number>(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const resultsContainerRef = useRef<HTMLDivElement>(null);

    const onDismiss = useCallback(() => {
        dispatch(setOverlayId(""));
        dispatch(clearSearchResults());
        dispatch(setDisplayHeaderSearch(false));
    }, [dispatch]);

    const onLoadMoreClicked = useCallback(() => {
        if (!stateProps.lastParams) {
            return;
        }

        const searchParams = {
            ...stateProps.lastParams,
            skip: stateProps.results?.length,
        };

        dispatch(executeSearchAction(searchParams));
    }, [dispatch, stateProps.lastParams, stateProps.results?.length]);

    const onResultClicked = useCallback((item: Spintr.ISearchResult) => {
        if (!item || !stateProps.lastParams || !stateProps.headers) {
            return;
        }

        if (location.pathname === "/") {
            mixpanelTrack("Search item clicked");
        }

        const isInternal = item.url.startsWith(_window?.location.origin ?? "/") ||
                           item.url.startsWith("/");

        if (!isInternal) {
            window.open(item.url);
        }

        const feedback: ISearchFeedback = {
            objectId: item.id,
            query: stateProps.lastParams?.query || "",
            totalHits: stateProps.headers?.totalCount || 0,
        };
        
        setState((prevState) => ({
            ...prevState,
            queryText: "",
            isSearchingQueryText: "",
            typeId: 0,
            aiSummary: {
                summary: undefined,
                generating: false,
                feedbackId: undefined,
                feedbackLiked: undefined,
                feedbackMessage: undefined,
            },
        }));

        submitFeedback(feedback);
        onDismiss();
    }, [stateProps.lastParams, stateProps.headers, location.pathname, _window?.location.origin, onDismiss]);

    const postAISearchFeedback = useCallback(
        (feedbackId: string, like?: boolean, searchQuery?: string, aiSummary?: string, feedbackMessage?: string, wellFoundedResult?: boolean) => {
            api.post(`/api/v1/feedback/ai-search`, {
                id: feedbackId,
                isPositive: like,
                searchQuery: searchQuery,
                aiSummary: wellFoundedResult || state.aiSummary?.summary?.wellFoundedResult ? aiSummary : undefined,
                feedbackMessage: feedbackMessage,
            }).catch((e) => {
                console.error("Failed to submit AI search feedback", e);
            });

            dispatch(
                setSearchAiSearchFeedback(
                    feedbackId,
                    like,
                    feedbackMessage,
                    currentChecksum.current ?? undefined
                )
            );
        },
        [dispatch, state.aiSummary?.summary?.wellFoundedResult]
    );

    const generateAiSummary = useCallback(async (
        query: string,
        documents: Spintr.ISearchResult[],
        hashCode: number
    ) => {
        if (state.aiSummary?.generating) {
            return;
        }

        const allowedTypes: Spintr.UberType[] = [
            SpintrTypes.UberType.NewsArticle,
            SpintrTypes.UberType.TextPage,
            SpintrTypes.UberType.WikiArticle,
            SpintrTypes.UberType.User,
        ];

        const filteredDocs = (documents || [])
            .filter((doc) => allowedTypes.includes(doc.type))
            .slice(0, 5);

        if (filteredDocs.length === 0) {
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    ...prevState.aiSummary,
                    summary: undefined,
                    generating: false,
                },
            }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            aiSummary: {
                ...prevState.aiSummary,
                summary: undefined,
                generating: true,
            },
        }));

        const abortController = new AbortController();

        let summaryText = "";

        await fetchEventSource("/api/v1/insights/search-summary", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                documents: filteredDocs,
                query: query,
            }),
            signal: abortController.signal,

            onerror: (err) => {
                abortController.abort();
                setState((prevState) => ({
                    ...prevState,
                    aiSummary: {
                        ...prevState.aiSummary,
                        summary: undefined,
                        generating: false,
                    },
                }));
                throw err;
            },

            onmessage: (event) => {
                summaryText += event.data;
                
                setState((prevState) => ({
                    ...prevState,
                    aiSummary: {
                        ...prevState.aiSummary,
                        summary: optimisticParse(summaryText),
                        generating: true,
                    },
                }));
            },

            onopen: async (response) => {
                if (response.ok) {
                    return;
                }

                throw new Error(`Failed to open connection: ${response.status} ${response.statusText}`);
            },

            onclose: () => {
                const parsedSummary = optimisticParse(summaryText);

                setState((prevState) => ({
                    ...prevState,
                    aiSummary: {
                        ...prevState.aiSummary,
                        summary: parsedSummary,
                        generating: false,
                    }
                }));
                
                postAISearchFeedback(
                    currentFeedbackId?.current || state.aiSummary?.feedbackId || "",
                    undefined,
                    query,
                    parsedSummary?.summary,
                    undefined,
                    parsedSummary?.wellFoundedResult
                );

                dispatch(setSearchAiSummary(parsedSummary, hashCode));
            },
        });
    }, [setState, dispatch, state.aiSummary?.generating, state.aiSummary?.feedbackId, postAISearchFeedback]);

    const executeGoogleSearch = useCallback(
        async (queryText: string) => {
            if (!stateProps.googleDriveEnabled) {
                return;
            }

            setState((s: MainHeaderSearchState) => ({
                ...s,
                googleIsLoading: true,
            }));

            api.get<GoogleFilesResponse>("/api/v1/search/google/files", {
                params: { query: queryText, take: 100 } as GoogleFilesRequest,
            })
                .then((response) => {
                    setState((prevState) => ({
                        ...prevState,
                        googleCount: response.data.items.length,
                        googleHasMore: !!response.data.nextPageToken,
                        googleIsLoading: false,
                    }));
                })
                .catch(() => {
                    setState((prevState) => ({
                        ...prevState,
                        googleIsLoading: false,
                    }));
                });
        },
        [stateProps.googleDriveEnabled]
    );

    const onOffice365Fetched = useCallback((envelopes: Spintr.IOffice365FileSearchEnvelope[] | null) => {
        if (envelopes === null) {
            // we caught a HTTP error
            return;
        }

        const oneDrive = envelopes.find((envelope) => envelope.serviceType === 1);
        const sharepoint = envelopes.find((envelope) => envelope.serviceType === 2);
        
        setState((prevState) => ({
            ...prevState,
            isLoadingOffice365: false,
            office365Count: (oneDrive ? oneDrive.items.length : 0) + (sharepoint ? sharepoint.items.length : 0),
            office365HasMore: ((oneDrive && oneDrive.hasMore) || (sharepoint && sharepoint.hasMore)) ?? false,
        }));
    }, [setState]);

    const executeOffice365Search = useCallback(
        (queryText: string) => {
            if (!stateProps.office365Enabled || (!stateProps.enableSharepoint && !stateProps.enableOnedrive)) {
                return;
            }

            const promises: Array<Promise<Spintr.IOffice365FileSearchEnvelope>> = [];

            if (stateProps.enableOnedrive) {
                promises.push(
                    searchFiles({
                        ignoreEmptyQuery: true,
                        orderByLatest: false,
                        query: queryText,
                        serviceType: 1,
                        skip: 0,
                        take: 100,
                    })
                );
            }

            if (stateProps.enableSharepoint) {
                promises.push(
                    searchFiles({
                        ignoreEmptyQuery: true,
                        orderByLatest: false,
                        query: queryText,
                        serviceType: 2,
                        skip: 0,
                        take: 100,
                    })
                );
            }

            setState((prevState) => ({
                ...prevState,
                isLoadingOffice365: true,
                office365Count: 0,
            }));

            from(Promise.all(promises))
                .pipe(catchError(() => of(null)))
                .subscribe(onOffice365Fetched);
        },
        [onOffice365Fetched, stateProps.enableOnedrive, stateProps.enableSharepoint, stateProps.office365Enabled]
    );

    const executeSearch = useCallback(async () => {
        const queryText = state.queryText || state.isSearchingQueryText;

        if (stateProps.isSearching || !queryText || queryText.length < 2) {
            return;
        }

        // Check if this is a new search query (different from the last one)
        const isNewQuery = queryText !== stateProps.lastParams?.query;

        // Determine the typeId to use for this search
        const searchTypeId = isNewQuery ? 0 : state.typeId;

        // Capture current height before starting search to prevent jumping
        if (resultsContainerRef.current && !stateProps.isSearching) {
            const currentHeight = resultsContainerRef.current.offsetHeight;
            if (currentHeight > 0) {
                setMinResultsHeight(currentHeight);
            }
        }

        executeOffice365Search(queryText);
        executeGoogleSearch(queryText);

        // only display the headerSearch if we are not on the /search page, note that it can have parameters, like ?q=blabla
        if (!location.pathname.startsWith("/search")) {
            dispatch(setDisplayHeaderSearch(true));
        }

        const searchParams: ISearchParams = {
            sort: 0,

            ...(stateProps.lastParams || {}),

            includeAiResults: false,
            query: queryText,
            objectType: searchTypeId,
            take: 15,
            skip: 0,
        };

        const code = checksum(searchParams);
        currentChecksum.current = code;

        if (stateProps.history[code]) {
            const cachedEntry = stateProps.history[code];
            dispatch(loadCachedSearchResult(code));
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    summary: cachedEntry.aiSummary,
                    generating: false,
                    feedbackId: cachedEntry.feedbackId,
                    feedbackLiked: cachedEntry.feedbackLiked,
                    feedbackMessage: cachedEntry.feedbackMessage,
                    feedbackMessageSent: cachedEntry.feedbackMessageSent,
                },
            }));
            return;
        }

        if (isNewQuery) {
            const feedbackId = uuid();
            currentFeedbackId.current = feedbackId;
            
            postAISearchFeedback(feedbackId, undefined, queryText, undefined, undefined);
        }

        setState((prevState) => ({
            ...prevState,
            queryText: "",
            isSearchingQueryText: queryText,
            // Reset typeId to 0 (All) only for new queries
            typeId: searchTypeId,
            aiSummary: {
                ...prevState.aiSummary,
                generating: prevState.aiSummary?.generating ?? false,
                feedbackId: currentFeedbackId?.current ?? "",
                ...(isNewQuery
                    ? {
                          feedbackLiked: undefined,
                          feedbackMessage: undefined,
                          feedbackMessageSent: undefined,
                      }
                    : {}),
            },
        }));

        const searchActionResult = await dispatch(executeSearchAction(searchParams));

        if (
            "action" in searchActionResult &&
            searchActionResult.action?.type !== SearchActionTypes.ExecuteSearchFulfilled
        ) {
            return;
        }

        if (location.pathname === "/") {
            mixpanelTrack("Search");
        }

        if (searchParams.objectType) {
            return; // Only do AI summary for the main search
        }

        let documents: Spintr.ISearchResult[];
        const response = ("value" in searchActionResult && "data" in searchActionResult.value) ? searchActionResult.value.data : undefined;
        if (Array.isArray(response)) {
            documents = response;
        } else {
            documents = response?.aiResults || [];
            if (documents.length === 0) {
                documents = response?.spintrResults || [];
            }
        }

        if (documents.length <= 0) {
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    summary: undefined,
                    generating: false,
                    feedbackLiked: undefined,
                    feedbackMessage: undefined,
                },
                isSearchingQueryText: "",
            }));
            return; // Nothing to summarize
        }

        // Filter documents to only include enabled uber types for AI search
        const filteredDocuments = documents.filter((doc) =>
            stateProps.aiSearchEnabledUberTypes.includes(doc.type as SpintrTypes.UberType)
        );

        if (filteredDocuments.length === 0) {
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    summary: undefined,
                    generating: false,
                    showAll: false,
                },
                isSearchingQueryText: "",
            }));
            return; // No enabled uber types in results
        }

        generateAiSummary(queryText, filteredDocuments, code);
    }, [
        state.queryText,
        state.isSearchingQueryText,
        state.typeId,
        stateProps.isSearching,
        stateProps.lastParams,
        stateProps.history,
        stateProps.aiSearchEnabledUberTypes,
        executeOffice365Search,
        executeGoogleSearch,
        location.pathname,
        dispatch,
        generateAiSummary,
        postAISearchFeedback,
    ]);

    const onQueryTextChanged = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
            setState((prevState) => ({
            ...prevState,
            queryText: event.target?.value || "",
        }));

        dispatch(setOverlayId(event.target?.value && !location.pathname.startsWith("/search") ? "search" : ""));
    }, [dispatch, location.pathname]);

    const onQueryInputKeyPressed = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
        (event) => event.stopPropagation(), []);

    const onQueryInputKeyReleased = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
        (event) => {
            if (event.keyCode === SpintrTypes.KeyCodes.Escape) {
                onDismiss();
                return;
            }

            if (event.keyCode !== SpintrTypes.KeyCodes.Enter) {
                return;
            }

            executeSearch();
        }, [executeSearch, onDismiss]);

    const onTypeChanged = useCallback((type?: number) => {
        if (type === undefined || type === null || isNaN(type)) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            typeId: type,
        }));
    }, [setState]);

    const saveAiSearchFeedback = useCallback(
        (like?: boolean) => {
            postAISearchFeedback(
                state.aiSummary?.feedbackId || "",
                like,
                stateProps.lastParams?.query ?? "",
                state.aiSummary?.summary?.summary,
                state.aiSummary?.feedbackMessage
            );
        },
        [state.aiSummary, stateProps.lastParams?.query, postAISearchFeedback]
    );

    const saveAiSearchFeedbackMessage = useCallback(
        (like?: boolean) => {
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    ...prevState.aiSummary,
                    generating: prevState.aiSummary?.generating ?? false,
                    feedbackMessageSent: true,
                },
            }));

            saveAiSearchFeedback(like);

            dispatch(setSearchAiSearchFeedbackMessage(true, currentChecksum.current ?? undefined));
        },
        [saveAiSearchFeedback, setState, dispatch]
    );

    const toggleLikeAiSummary = useCallback(
        (like?: boolean) => {
            const nextLike = like === state.aiSummary?.feedbackLiked ? undefined : like;

            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    ...prevState.aiSummary,
                    generating: prevState.aiSummary?.generating ?? false,
                    feedbackLiked: nextLike,
                    feedbackMessageSent: false
                },
            }));

            saveAiSearchFeedback(nextLike);
        },
        [state.aiSummary?.feedbackLiked, saveAiSearchFeedback]
    );

    const onShowInternalHitsClick = () => {
        mixpanelTrack("Search - Clicked show hits from intranet");
    }

    const onShow365ResultsClick = () => {
        mixpanelTrack("Search - Clicked show Office 365 results");
    }

    useEffect(() => {
        setState({
            ...state,
        });
        executeSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.typeId]);

    useEffect(() => {
        if (!state.isSearchingQueryText) {
            return;
        }
        executeSearch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isSearchingQueryText]);

    const placeholderText = useMemo(() => localize("MODERN_SEARCH_PLACEHOLDER"), []);

    const types = useMemo(
        () => stateProps.headers && stateProps.headers?.objectTypes
            ?  Object.keys(stateProps.headers.objectTypes)
                .filter((key) => !isNaN(+key))
                .map((key) => parseInt(key, 10))
                .filter((key) => key !== SpintrTypes.UberType.ChatMessage)
                .map((key) => ({
                    key,
                    selected: false,
                    text: key === SpintrTypes.UberType.Unknown
                        ? localize("Alla")
                        : capitalizeFirstLetter(getTypeName(key, {
                            case: "indefinite",
                            form: "plural",
                        })),
                    count: stateProps.headers?.objectTypes?.[key] || 0,
                }))
            : [],
        [stateProps.headers]);

    useEffect(() => {
        const calloutEnabled = stateProps.displayHeaderSearch;

        if (!calloutEnabled || !_window) {
            return;
        }

        const de = _window.document.documentElement;
        const b = _window.document.body;

        b.classList.add("no-scroll");
        b.style.setProperty('--st', -(de.scrollTop) + "px");

        return () => {
            b.classList.remove("no-scroll");
        };
    }, [stateProps.displayHeaderSearch, _window]);

    // Clear minimum height when search completes
    useEffect(() => {
        if (!stateProps.isSearching && minResultsHeight > 0) {
            setMinResultsHeight(0);
        }
    }, [stateProps.isSearching, minResultsHeight]);

    const hasAISummaryDislikeMessage = useMemo(() => {
        return !!state.aiSummary?.feedbackMessage?.length;
    }, [state.aiSummary?.feedbackMessage]);

    const results = (stateProps.results ?? []).length > 0
        ? stateProps.results
        : (stateProps.aiResults ?? []);

    const hasResults = results && results.length > 0;

    const hasMore = stateProps.lastParams && (stateProps.lastParams.objectType === 0
        ? (stateProps.headers?.totalCount ?? 0) > (stateProps.results?.length ?? 0)
        : (stateProps.headers?.objectTypes?.[stateProps.lastParams?.objectType ?? 0] || 0) > (stateProps.results?.length ?? 0));

    const sidebarHidden = stateProps.isSearching ||
                          !results ||
                          results?.length <= 0

    return (
        <div
            id="MainHeaderSearch"
            className={classNames({
                sidebarHidden: sidebarHidden,
                hasSearched: !!state.isSearchingQueryText || stateProps.lastParams,
            })}
            ref={ref}
        >
            <Conditional condition={stateProps.displayHeaderSearch}>
                <FocusTrapCallout
                    role="alertdialog"
                    target=".search-bar"
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    className={classNames("main-header-search-callout", {
                        isSearching: stateProps.isSearching,
                    })}
                    layerProps={{ eventBubblingEnabled: true }}
                    gapSpace={8}
                    calloutWidth={750}
                    onDismiss={onDismiss}
                    preventDismissOnScroll
                    dismissOnTargetClick
                    focusTrapProps={{ isClickableOutsideFocusTrap: true, disableFirstFocus: true }}
                >
                    <div
                        className="results-container"
                        ref={resultsContainerRef}
                        style={minResultsHeight > 0 ? { minHeight: `${minResultsHeight}px` } : undefined}
                    >
                        <div className="top">
                            <Conditional condition={state.isSearchingQueryText || !!stateProps.lastParams?.query}>
                                <div className="query-container">
                                    <SmallBody weight="semiBold">
                                        {state.isSearchingQueryText || stateProps.lastParams?.query}
                                    </SmallBody>
                                </div>
                            </Conditional>
                            <Conditional condition={stateProps.isSearching}>
                                <SpintrLoader />
                            </Conditional>
                            <Conditional condition={!stateProps.isSearching && !hasResults && stateProps.lastParams}>
                                <SmallBody>{localize("MODERN_SEARCH_NO_RESULTS")}</SmallBody>
                            </Conditional>
                            {stateProps.errorMessageTag && (
                                <SmallBody>{localize(stateProps.errorMessageTag)}</SmallBody>
                            )}
                            <Conditional condition={!stateProps.isSearching && hasResults}>
                                <Conditional
                                    condition={!!state.aiSummary?.summary?.summary || state.aiSummary?.generating}
                                >
                                    <SmallBody className={classNames("ai-summary")}>
                                        <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 6 }}>
                                            <Visage2Icon
                                                className="ai-summary-icon"
                                                color="transparent"
                                                icon="sparkling-outline"
                                                type="custom"
                                            />
                                            <StackItem grow>
                                                {state.aiSummary?.generating &&
                                                    (state.aiSummary?.summary?.summary ?? "").length == 0 && (
                                                        <>
                                                            <Shimmer />
                                                            <ShimmerGap height={2} />
                                                            <Shimmer width="75%" />
                                                        </>
                                                    )}
                                                <div
                                                    tabIndex={-1}
                                                    dangerouslySetInnerHTML={{
                                                        __html: (() => {
                                                            return state.aiSummary?.summary?.summary || "";
                                                        })(),
                                                    }}
                                                ></div>
                                                {!state.aiSummary?.generating && (
                                                    <TextBubble
                                                        tagText={localize("NOTE")}
                                                        text={localize("AI_CAN_BE_WRONG")}
                                                        inline
                                                        margin={{ top: "medium" }}
                                                    />
                                                )}
                                            </StackItem>
                                            <StackItem
                                                className={classNames("ai-summary-feedback", {
                                                    "not-visible": state.aiSummary?.generating,
                                                })}
                                                align="center"
                                            >
                                                {!state.aiSummary?.generating && (
                                                    <>
                                                        {localize("DID_AI_ANSWER_HELP")}
                                                        <div>
                                                            <UnstyledButton
                                                                className={classNames(
                                                                    "ai-summary-feedback-button",
                                                                    "like",
                                                                    {
                                                                        "like--checked":
                                                                            state.aiSummary?.feedbackLiked === true,
                                                                    }
                                                                )}
                                                                onClick={toggleLikeAiSummary}
                                                                onClickData={true}
                                                                ariaLabel={localize("LIKE_AISUMMARY")}
                                                                ariaPressed={state.aiSummary?.feedbackLiked === true}
                                                            >
                                                                <div className="particle particle-1">+</div>
                                                                <div className="particle particle-2">*</div>
                                                                <div className="particle particle-3">*</div>
                                                                <div className="particle particle-4">+</div>
                                                                <div className="particle particle-5">•</div>
                                                                <div className="particle particle-6">•</div>
                                                                <Visage2Icon
                                                                    icon="like-1"
                                                                    type={
                                                                        state.aiSummary?.feedbackLiked === true
                                                                            ? "bold"
                                                                            : undefined
                                                                    }
                                                                    color={
                                                                        state.aiSummary?.feedbackLiked === true
                                                                            ? "white"
                                                                            : undefined
                                                                    }
                                                                />
                                                            </UnstyledButton>
                                                            <UnstyledButton
                                                                className={classNames(
                                                                    "ai-summary-feedback-button",
                                                                    "dislike",
                                                                    {
                                                                        "dislike--checked":
                                                                            state.aiSummary?.feedbackLiked === false,
                                                                    }
                                                                )}
                                                                onClick={toggleLikeAiSummary}
                                                                onClickData={false}
                                                                ariaLabel={localize("DISLIKE_AISUMMARY")}
                                                                ariaPressed={state.aiSummary?.feedbackLiked === false}
                                                            >
                                                                <Visage2Icon
                                                                    icon="dislike"
                                                                    type={
                                                                        state.aiSummary?.feedbackLiked === false
                                                                            ? "bold"
                                                                            : undefined
                                                                    }
                                                                    color={
                                                                        state.aiSummary?.feedbackLiked === false
                                                                            ? "white"
                                                                            : undefined
                                                                    }
                                                                />
                                                            </UnstyledButton>
                                                        </div>
                                                        <div className="ai-summary-feedback-labels">
                                                            <span>{localize("Ja")}</span>
                                                            <span>{localize("Nej")}</span>
                                                        </div>
                                                        {state.aiSummary?.feedbackLiked === false && (
                                                            <div className="ai-summary-feedback-input">
                                                                {!state.aiSummary.feedbackMessageSent ? (
                                                                    <>
                                                                        <FormControl>
                                                                            <TextField
                                                                                value={
                                                                                    state.aiSummary?.feedbackMessage ??
                                                                                    ""
                                                                                }
                                                                                placeholder={localize(
                                                                                    "SkrivEnKommentar"
                                                                                )}
                                                                                onChange={(_, val) => {
                                                                                    setState((prevState) => ({
                                                                                        ...prevState,
                                                                                        aiSummary: {
                                                                                            ...prevState.aiSummary,
                                                                                            generating:
                                                                                                prevState.aiSummary
                                                                                                    ?.generating ??
                                                                                                false,
                                                                                            feedbackMessage: val,
                                                                                        },
                                                                                    }));
                                                                                }}
                                                                                maxLength={256}
                                                                                aria-required
                                                                                aria-multiline
                                                                                multiline
                                                                                autoAdjustHeight
                                                                            />
                                                                        </FormControl>
                                                                        <UnstyledButton
                                                                            onClick={saveAiSearchFeedbackMessage}
                                                                            onClickData={state.aiSummary?.feedbackLiked}
                                                                            disabled={!hasAISummaryDislikeMessage}
                                                                        >
                                                                            <Visage2Icon
                                                                                icon="send-1"
                                                                                type="bold"
                                                                                color={
                                                                                    hasAISummaryDislikeMessage
                                                                                        ? undefined
                                                                                        : "dusk-grey"
                                                                                }
                                                                                className={
                                                                                    hasAISummaryDislikeMessage
                                                                                        ? "primaryVisageIconColorFill"
                                                                                        : undefined
                                                                                }
                                                                            />
                                                                        </UnstyledButton>
                                                                    </>
                                                                ) : (
                                                                    <Label>{localize("FEEDBACK_SUCCESS")}</Label>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </StackItem>
                                        </Stack>
                                    </SmallBody>
                                </Conditional>
                            </Conditional>
                        </div>
                        <Conditional condition={
                            !stateProps.isSearching && 
                            (hasResults || (state.isLoadingOffice365 || state.office365Count > 0) || (state.googleIsLoading || state.googleCount > 0))
                        }>
                            <div className="results">
                                <div className="left">
                                    <Conditional condition={(results?.length ?? 0) > 0}>
                                        <ol className="list">
                                            {results?.map((result) => (
                                                <li className="item" key={`HeaderSearch.Result${result.id}`}>
                                                    <ModernSearchResultObjectHit
                                                        item={result}
                                                        onClick={onResultClicked}
                                                    />
                                                </li>
                                            ))}
                                        </ol>
                                    </Conditional>
                                    <Conditional condition={stateProps.isSearchingMore}>
                                        <SpintrLoader />
                                    </Conditional>
                                    <Conditional condition={hasMore && !stateProps.isSearchingMore}>
                                        <UnstyledButton
                                            className="load-more-button ModernSearchResultObjectHit"
                                            onClick={onLoadMoreClicked}
                                        >
                                            <SmallBodyBolder color="blue">{localize("VisaFler")}</SmallBodyBolder>
                                            <Visage2Icon color="visageMidBlue" icon="arrow-down-1" />
                                        </UnstyledButton>
                                    </Conditional>
                                    <div className="view-all ModernSearchResultObjectHit">
                                        {hasResults && (
                                            <Link
                                                to={
                                                    "/search?q=" +
                                                    encodeURIComponent(stateProps.lastParams?.query || "")
                                                }
                                                onClick={onShowInternalHitsClick}
                                            >
                                                <SmallBodyBolder color="blue">
                                                    {localize("GROUP_SEARCH_SHOW_ALL")}(
                                                    {stateProps.headers?.totalCount})
                                                </SmallBodyBolder>
                                            </Link>
                                        )}
                                        {state.isLoadingOffice365 || state.office365Count > 0 && (
                                            <Link
                                                to={
                                                    "/search?s=365&q=" +
                                                    encodeURIComponent(stateProps.lastParams?.query || "")
                                                }
                                                className="office-365-results-link"
                                                onClick={onShow365ResultsClick}
                                            >
                                                <Icon iconName="OfficeLogo" />
                                                <SmallBodyBolder as="span">
                                                    {state.isLoadingOffice365 && state.office365Count === 0
                                                        ? localize("Laddar") + "..."
                                                        : localizeFormat(
                                                                "SEARCH_SHOW_X_O365_RESULTS_F",
                                                                state.office365Count +
                                                                    (state.office365HasMore ? "+" : "")
                                                            )}
                                                </SmallBodyBolder>
                                            </Link>
                                        )}
                                        {state.googleIsLoading || state.googleCount > 0 && (
                                            <Link
                                                to={
                                                    "/search?s=google&q=" +
                                                    encodeURIComponent(stateProps.lastParams?.query || "")
                                                }
                                                className="google-results-link"
                                                onClick={onShow365ResultsClick}
                                            >
                                                <Visage2Icon icon="google" type="custom" />
                                                <SmallBodyBolder as="span">
                                                    {state.googleIsLoading && state.googleCount === 0
                                                        ? localize("Laddar") + "..."
                                                        : localizeFormat(
                                                                "SEARCH_SHOW_X_GOOGLE_RESULTS_F",
                                                                state.googleCount +
                                                                    (state.googleHasMore ? "+" : "")
                                                            )}
                                                </SmallBodyBolder>
                                            </Link>
                                        )}
                                    </div>
                                </div>
                                <Conditional condition={!sidebarHidden}>
                                    <div className="right">
                                        <div className="classic-results">
                                            <SmallBodyBolder className="classic-results-header" color="contentDark">
                                                {localize("Innehallstyper")}
                                            </SmallBodyBolder>
                                            {types.map((type) => (
                                                <UnstyledButton
                                                    key={type.key}
                                                    className="type"
                                                    onClick={onTypeChanged}
                                                    onClickData={type.key}
                                                >
                                                    <CaptionBody
                                                        color="blue"
                                                        weight={state.typeId === type.key ? "semiBold" : "medium"}
                                                    >
                                                        {type.text} ({type.count})
                                                    </CaptionBody>
                                                </UnstyledButton>
                                            ))}
                                        </div>
                                    </div>
                                </Conditional>
                            </div>
                        </Conditional>
                    </div>
                </FocusTrapCallout>
            </Conditional>
            <div className="MainHeaderSearch-content">
                <div className={classNames("left", { displayBorder: stateProps.headerColor === "#FFFFFF" })}>
                    <div className="search-bar">
                        <input
                            aria-label={placeholderText}
                            className="query-input"
                            name="query"
                            onChange={onQueryTextChanged}
                            onKeyDown={onQueryInputKeyPressed}
                            onKeyUp={onQueryInputKeyReleased}
                            placeholder={placeholderText}
                            ref={inputRef}
                            type="text"
                            value={state.queryText}
                        />
                    </div>
                    <div className="search-button-container">
                        <UnstyledButton className="search-button" onClick={executeSearch} title={localize("Sok")}>
                            <Conditional condition={!stateProps.isSearching && !state.aiSummary?.generating}>
                                <Visage2Icon icon="arrow-up-3" />
                            </Conditional>
                            <Conditional condition={stateProps.isSearching || state.aiSummary?.generating}>
                                <SearchLoader size={20} speed={100} />
                            </Conditional>
                        </UnstyledButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MainHeaderSearch;
